import React from "react";
import { ChangeEvent, FormEvent } from "react";
import { Prompt } from "react-router";
import { RouteComponentPropsHistory } from "../../types";

import { DateTime } from "luxon";

import { V2, apiGet, apiPatch, apiPost } from "../../Api";
import { filterStateCode, formatName, formatTelephonePartial, formatZipCode, TIME_ISO_BARE, validateEmailOptional, validatePhoneNumber, formatList } from "../../Utilities";
import { IAuthProps, WellKnownGroups } from "../AuthContext";
import withAuth from "../withAuth";
import withTitle, { IViewTitleProps } from "../withTitle";

import NoteBox from "../NoteBox";
import TagListInput from "../TagListInput";
import ValidateInput from "../ValidateInput";

import "../../blocks.css";
import "../../button.css";
import "../../form.css";
import "../../list.css";
import "../viewer.css";

interface IHealthFairEditState {
    isEditingNote: boolean;
    isLoading: boolean;

    members: any[];
    locationName: string;
    headCount: number | "";
    streetAddress: string;
    city: string;
    state: string;
    zip: string;
    scheduleDate: string;
    scheduleTime: string;
    hasChanged: boolean;
    hasNoteChanged: boolean;

    memberOptions: any[];
    submitError: string | null;
    existingFairId?: string;
    fairLoadError?: string;
    isSubmitting: boolean;

    contactName: string;
    contactPhone: string;
    contactEmail: string;

    notes?: string;
    postNotes: any[];

    companyId?: string;

    membersOnVacation: any[];
    membersOnVacationError?: string;
}

class AddHealthFairView extends React.Component<RouteComponentPropsHistory<any, any> & IViewTitleProps & IAuthProps, IHealthFairEditState> {
    private inputs: ValidateInput[];

    constructor(props: Readonly<RouteComponentPropsHistory<any, any> & IViewTitleProps & IAuthProps>) {
        super(props);

        let preloadDate = "";
        let preloadTime = "";

        if (props.location.state && props.location.state.date) {
            preloadDate = props.location.state.date;
        }
        if (props.location.state && props.location.state.time) {
            preloadTime = props.location.state.time;
        }

        this.state = {
            isEditingNote: false,
            isLoading: true,

            city: "",
            contactEmail: "",
            contactName: "",
            contactPhone: "",
            hasChanged: false,
            hasNoteChanged: false,
            headCount: "",
            locationName: "",
            memberOptions: [],
            members: [],
            notes: "",
            postNotes: [],
            scheduleDate: preloadDate,
            scheduleTime: preloadTime,
            state: "",
            streetAddress: "",
            zip: "",

            existingFairId: props.match.params.id,
            isSubmitting: false,
            submitError: null,

            membersOnVacation: [],
        };

        this.processForm = this.processForm.bind(this);
        this.handleMemberListChange = this.handleMemberListChange.bind(this);
        this.formInputChanged = this.formInputChanged.bind(this);
        this.formInputChangedNumber = this.formInputChangedNumber.bind(this);
        this.submitChanges = this.submitChanges.bind(this);
        this.cancelChanges = this.cancelChanges.bind(this);
        this.handleMemberSuggestions = this.handleMemberSuggestions.bind(this);
        this.handleRef = this.handleRef.bind(this);
        this.submitNote = this.submitNote.bind(this);
        this.noteEditorChange = this.noteEditorChange.bind(this);

        this.inputs = [];
    }

    public async componentDidMount() {
        try {
            const json = await apiGet<any[]>(`employees/summary?group=${WellKnownGroups.HfTeam}`);

            json.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)));

            this.setState({
                memberOptions: json
            });
        }
        catch (e) {
            this.setState({
                memberOptions: [
                    { id: 0, name: { first: "Could not load marketer list", last: "" } }
                ]
            });
        }

        const fairState = this.props.location.state && this.props.location.state.fair;
        const locationState = this.props.location.state && this.props.location.state.baseLocation;

        if (!this.state.existingFairId && !fairState) {
            // Creating a fair from a known location
            if (locationState) {
                this.setState({
                    isLoading: false,

                    city: locationState.address.city,
                    state: locationState.address.state,
                    streetAddress: locationState.address.street,
                    zip: locationState.address.zip,

                    contactEmail: locationState.contact ? locationState.contact.email : "",
                    contactName: locationState.contact ? locationState.contact.name : "",
                    contactPhone: locationState.contact ? locationState.contact.phone : "",

                    headCount: locationState.employeeCount,
                    locationName: locationState.name,

                    companyId: locationState.id,
                });
            }
            else {
                this.setState({
                    isLoading: false
                });
            }
            return;
        }

        if (this.state.existingFairId) {
            this.props.viewTitleContext.changeTitle("Edit Health Fair");
        }

        // Get health fair info for edit mode
        const baseFairId = this.state.existingFairId || fairState.id;

        try {
            // Create a new fair based on an old one
            const fair = await apiGet<any>(`fairs/${baseFairId}`);
            const nstate = this.props.location.state;

            fair.contact = fair.contact || {};
            if (fair.postNotes) {
                fair.postNotes.forEach((a: any) => a.author = formatName(a.employee.name));
            }

            fair.scheduledDate = DateTime.fromISO(fair.scheduledDate).toLocal();

            this.setState({
                city: fair.address.city,
                companyId: fair.companyId,
                contactEmail: fair.contact.email,
                contactName: fair.contact.name,
                contactPhone: fair.contact.phone,
                existingFairId: (nstate && nstate.fair) ? undefined : fair.id,
                // hasChanged: false,
                headCount: fair.estimatedHeadCount,
                locationName: fair.name,
                // memberOptions: [],
                members: fair.members,
                notes: fair.notes,
                postNotes: fairState ? [] : fair.postNotes,
                scheduleDate: (nstate && nstate.fair) ? "" : fair.scheduledDate.toISODate(),
                scheduleTime: (nstate && nstate.fair) ? "" : fair.scheduledDate.toISOTime(TIME_ISO_BARE),
                state: fair.address.state,
                streetAddress: fair.address.street,
                // submitError: null,
                zip: fair.address.zip,

                isLoading: false
            });
        }
        catch (e) {
            this.setState({
                fairLoadError: "Could not load health fair",
                isLoading: false
            });
        }
    }

    public render() {
        if (this.state.isLoading) {
            return (
                <div className="spinner-full-root">
                    <div className="spinner lds-dual-ring-big" />
                </div>
            );
        }

        if (this.state.fairLoadError) {
            return (
                <div className="viewer-root">
                    <span className="error-text">{this.state.fairLoadError}</span>
                </div>
            );
        }

        return (
            <div className="viewer-root with-actionbar">
                <Prompt when={this.state.hasChanged || this.state.isEditingNote} message="Your changes will not be saved if you continue." />
                <form className="input-form" onSubmit={this.processForm}>
                    <div className="column-root">
                        <section className="info-block">
                            <header>
                                <h3>Health Fair Details</h3>
                            </header>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-hf-date" className="required">Date</label>
                                    <ValidateInput type="date" id="frm-hf-date" ref={this.handleRef} name="scheduleDate" value={this.state.scheduleDate} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} validator={/^\d{4}-\d{2}-\d{2}$/} />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-hf-time" className="required">Time</label>
                                    <ValidateInput type="time" id="frm-hf-time" ref={this.handleRef} name="scheduleTime" value={this.state.scheduleTime} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} validator={/^\d{2}:\d{2}(?::\d{2})?$/} />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-hf-location" className="required">Name</label>
                                    <ValidateInput type="text" id="frm-hf-location" ref={this.handleRef} name="locationName" value={this.state.locationName} onChange={this.formInputChanged} placeholder="Name of location" autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-hf-head-count" className="required">Estimated head count</label>
                                    <ValidateInput type="number" id="frm-hf-head-count" ref={this.handleRef} name="headCount" value={this.state.headCount} onChange={this.formInputChangedNumber} placeholder="Number of people expected to attend" autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                            </section>
                        </section>
                        <section className="info-block">
                            <header>
                                <h3>Health Fair Location</h3>
                            </header>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-hf-addr-street" className="required">Address line 1</label>
                                    <ValidateInput type="text" id="frm-hf-addr-street" ref={this.handleRef} name="streetAddress" value={this.state.streetAddress} onChange={this.formInputChanged} placeholder="Street Address" autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-hf-addr-city" className="required">City</label>
                                    <ValidateInput type="text" id="frm-hf-addr-city" ref={this.handleRef} name="city" value={this.state.city} onChange={this.formInputChanged} placeholder="City" autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                            </section>
                            <section className="always-row">
                                <div className="input-form-group">
                                    <label htmlFor="frm-hf-addr-state" className="required">State</label>
                                    <ValidateInput type="text" id="frm-hf-addr-state" ref={this.handleRef} name="state" value={this.state.state} onChange={this.formInputChanged} placeholder="State (e.g. GA)" autoComplete="off_please_chrome" isRequired={true} filter={filterStateCode} />
                                </div>
                                <div className="input-form-group input-form-group-small">
                                    <label htmlFor="frm-hf-addr-zip" className="required">ZIP code</label>
                                    <ValidateInput type="text" id="frm-hf-addr-zip" ref={this.handleRef} name="zip" value={this.state.zip} onChange={this.formInputChanged} placeholder="5 digit ZIP" autoComplete="off_please_chrome" isRequired={true} filter={formatZipCode} validator={/^\d{5}$/} />
                                </div>
                            </section>
                        </section>
                    </div>
                    <br />
                    <div className="column-root">
                        <section className="info-block">
                            <header>
                                <h3>Company Info</h3>
                            </header>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-hf-contact-name" className="required">Contact Name</label>
                                    <ValidateInput type="text" id="frm-hf-contact-name" ref={this.handleRef} name="contactName" value={this.state.contactName} onChange={this.formInputChanged} placeholder="Name of contact person" autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-hf-contact-phone" className="required">Phone number</label>
                                    <ValidateInput type="text" id="frm-hf-contact-phone" ref={this.handleRef} name="contactPhone" value={this.state.contactPhone} onChange={this.formInputChanged} placeholder="(xxx) xxx-xxxx" autoComplete="off_please_chrome" isRequired={true} filter={formatTelephonePartial} requiredMessage="Phone number is required" validator={validatePhoneNumber} />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-hf-contact-email">Email Address</label>
                                    <ValidateInput type="text" id="frm-hf-contact-email" ref={this.handleRef} name="contactEmail" value={this.state.contactEmail} onChange={this.formInputChanged} placeholder="Contact email (optional)" autoComplete="off_please_chrome" isRequired={false} validator={validateEmailOptional} />
                                </div>
                            </section>
                        </section>
                        <section className="info-block">
                            <header>
                                <h3>Other</h3>
                            </header>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-hf-marketers">Assigned marketers</label>
                                    <TagListInput id="frm-hf-marketers" displayMember="name" items={this.state.members.map(a => ({ id: a.id, name: typeof a.name === "string" ? a.name : formatName(a.name) }))} onItemsChange={this.handleMemberListChange} requestOptions={this.handleMemberSuggestions} />
                                    {this.state.membersOnVacationError
                                        ? <div className="warning-box slim nomargin" style={{ marginTop: "0.5em" }}>
                                            {this.state.membersOnVacationError}
                                        </div>
                                        : this.state.membersOnVacation.length > 0
                                            ? <div className="warning-box slim nomargin" style={{ marginTop: "0.5em" }}>
                                                {formatList(this.state.membersOnVacation, (item, i) => i % 2 === 0 ? <b key={item.id}>{item.name}</b> : item)} {this.state.membersOnVacation.length === 1 ? "is" : "are"} off this day.
                                            </div>
                                            : null}
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-hf-notes">Fair-specific notes</label>
                                    <textarea id="frm-hf-notes" name="notes" value={this.state.notes} onChange={this.formInputChanged} placeholder="Enter a note for this specific health fair..." autoComplete="off_please_chrome" />
                                </div>
                            </section>
                            {
                                this.state.existingFairId
                                    ? <section>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-hf-postnotes">Follow-up Notes</label>
                                            <NoteBox displayName={this.props.authContext.user.displayName} notes={this.state.postNotes} onNewNote={this.submitNote} hideControls={!this.props.authContext.hasRight("appt:write")} onEditorChange={this.noteEditorChange} />

                                        </div>
                                    </section>
                                    : ""
                            }
                        </section>
                    </div>
                </form>
                &nbsp;
                <span className="error-text">{this.state.submitError}</span>
                <div className="action-panel">
                    <button className="button blue btn-confirm push-right" onClick={this.submitChanges} disabled={this.state.isSubmitting}>Save</button>
                    <button className="button blue btn-cancel" onClick={this.cancelChanges} disabled={this.state.isSubmitting}>Cancel</button>
                </div>
            </div>
        );
    }

    private processForm(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
    }

    private handleMemberListChange(items: any[]) {
        this.setState({
            hasChanged: true,
            members: items
        }, () => this.updateMemberOffList());
    }

    private handleMemberSuggestions(text: string) {
        return this.state.memberOptions
            .map(a => ({ id: a.id, name: formatName(a.name) }))
            .filter(a => !text || a.name.toLowerCase().includes(text.toLowerCase()));
    }

    private async updateMemberOffList() {
        try {
            const vacation = this.state.scheduleDate && this.state.members.length > 0 ? await V2.get<any[]>(`schedule/vacation?after=${this.state.scheduleDate}&before=${this.state.scheduleDate}&group=${WellKnownGroups.HfTeam}`) : [];
            this.setState({
                membersOnVacationError: undefined,
                membersOnVacation: vacation.filter(a => this.state.members.some(b => a.employee.id === b.id)).map(a => ({
                    id: a.employee.id,
                    name: formatName(a.employee.name)
                }))
            });
        }
        catch (e) {
            this.setState({
                membersOnVacationError: `Could not check marketer vacation: ${(e.message || e || "Unknown reason")}`
            });
        }
    }

    private formInputChanged(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;

        this.setState({
            [name]: value,
            hasChanged: true
        } as any, () => {
            if (name === "scheduleDate") {
                this.updateMemberOffList();
            }
        });
    }

    private formInputChangedNumber(e: ChangeEvent<HTMLInputElement>) {
        const name = e.currentTarget.name;
        const value = parseInt(e.currentTarget.value, 10);

        this.setState({
            [name]: isNaN(value) ? "" : value,
            hasChanged: true
        } as any);
    }

    private async submitNote(body: string) {
        const note = {
            body
        };
        const noteResult = await apiPost<any>(`fairs/${this.state.existingFairId}/notes`, note);
        noteResult.author = formatName(noteResult.employee.name);

        this.setState(s => {
            s.postNotes.push(noteResult);

            return {
                postNotes: s.postNotes,
            }
        });
    }

    private noteEditorChange(isEditing: boolean) {
        this.setState({
            isEditingNote: isEditing
        });
    }

    private submitChanges() {
        let allValid = true;
        for (const input of this.inputs) {
            allValid = input.validate() && allValid;
        }

        if (!allValid) {
            return;
        }

        this.setState({
            isSubmitting: true
        });

        const output = {
            address: {
                city: this.state.city,
                state: this.state.state,
                street: this.state.streetAddress,
                zip: this.state.zip
            },
            companyId: this.state.companyId,
            contact: {
                email: this.state.existingFairId ? this.state.contactEmail : (this.state.contactEmail || undefined), // Send blank email on edit
                name: this.state.contactName || undefined,
                phone: this.state.contactPhone ? this.state.contactPhone.replace(/[^\d]/g, "") : undefined
            },
            estimatedHeadCount: this.state.headCount,
            members: this.state.members,
            name: this.state.locationName,
            notes: this.state.notes,
            scheduledDate: DateTime.fromISO(`${this.state.scheduleDate}T${this.state.scheduleTime}`)
        };

        (this.state.existingFairId ?
            apiPatch(`fairs/${this.state.existingFairId}`, output) :
            apiPost("fairs", output))
            .then(r => {
                this.setState({
                    hasChanged: false,
                    isSubmitting: false
                }, () => {
                    if (this.state.existingFairId) {
                        this.props.history.goBack();
                    }
                    else {
                        // Send user back to the schedule on the month they started on
                        this.props.history.replace("/fairs/schedule", { month: this.state.scheduleDate.substr(0, 7) });
                    }
                }
                );
            }).catch(e => {
                this.setState({
                    isSubmitting: false,
                    submitError: e.message
                });
            });
    }

    private cancelChanges() {
        this.props.history.goBack();
    }

    private handleRef(input: ValidateInput) {
        this.inputs.push(input);
    }


}

export default withTitle("Add Health Fair", withAuth(AddHealthFairView));
